import { useState, useContext } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { MessageContext } from "../../context/MessageContext";
import { buildApiUrl } from "../../config/api";

interface ResetPasswordProps {
  visible: boolean;
  onHide: () => void;
}

const ResetPassword = ({ visible, onHide }: ResetPasswordProps) => {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { pushMessageForToast } = useContext(MessageContext);

  const handleReset = () => {
    setError("");
    setEmailOrUsername("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(buildApiUrl("/api/reset-password/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_or_username: emailOrUsername,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        pushMessageForToast({
          severity: "success",
          message: data.message || "Password reset successfully!",
        });
        handleReset();
        onHide();
      } else {
        setError(data.error || "Failed to reset password");
      }
    } catch {
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      header="Reset Password"
      modal
      onHide={() => {
        handleReset();
        onHide();
      }}
      className="w-4">
      <form onSubmit={handleSubmit} className="flex flex-column gap-4 pt-3">
        <div className="flex flex-column gap-2">
          <label>Email or Username</label>
          <InputText
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
            required
            className="w-full"
            placeholder="Enter email or username"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label>New Password</label>
          <Password
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            toggleMask
            required
            className="w-full"
            inputClassName="w-full"
            placeholder="Enter new password"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label>Confirm Password</label>
          <Password
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            feedback={false}
            toggleMask
            required
            className="w-full"
            inputClassName="w-full"
            placeholder="Confirm new password"
          />
        </div>

        {error && <Message severity="error" text={error} />}

        <div className="flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            type="button"
            severity="secondary"
            onClick={() => {
              handleReset();
              onHide();
            }}
          />
          <Button
            label={loading ? "Resetting..." : "Reset Password"}
            type="submit"
            loading={loading}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default ResetPassword;
